import ErrorMessage from "@medoma/components/ErrorMessage/ErrorMessage";
import { hasAuthParams, useAuth } from "react-oidc-context";
import { useCallback, useEffect, useLayoutEffect } from "react";
import { Loading } from "@medoma/components/Loading/Loading";
import { FilledButton } from "@medoma/components/Button/Button";
import * as Sentry from "@sentry/react";
import { deducedError } from "@/Utils/ErrorUtils";
import { interactiveSignin } from "@/Utils/interactiveSignin";
import { Trans, useLingui } from "@lingui/react/macro";
import { clearOtherUsersSessionStorage } from "@medoma/shared-employee/userSessionStorage";

export const RequireGoAuth = ({ children }: { children: React.ReactNode }) => {
  const { t } = useLingui();
  const auth = useAuth();
  const { error, isAuthenticated, activeNavigator, isLoading, events, user } =
    auth;

  const signinArgs = useCallback(
    () => ({
      url_state: user?.profile.sub,
      state: window.location.pathname,
      acr_values: "mfa",
    }),
    [user],
  );

  const signin = useCallback(() => {
    void interactiveSignin({ via: auth, ...signinArgs() });
  }, [auth, signinArgs]);

  // A layout effect, so that the redirect's synchronous `activeNavigator` update
  // renders the loading state before the browser paints the sign-in button.
  useLayoutEffect(() => {
    if (
      !hasAuthParams() &&
      !isAuthenticated &&
      !activeNavigator &&
      !isLoading
    ) {
      signin();
    }
  }, [isAuthenticated, activeNavigator, isLoading, signin]);

  // Token can expire while the app is in background
  // Forcefully sign in again if this happens
  useEffect(() => {
    return events.addAccessTokenExpired(signin);
  }, [events, signin]);

  if (activeNavigator || isLoading) {
    return <Loading message={t`Verifierar behörighet`} padding={24} />;
  }

  if (isAuthenticated && user) {
    // The gate every signed-in route passes, so another employee's tab-scoped
    // storage is gone before any page reads its key.
    clearOtherUsersSessionStorage(user.profile.sub);
    return children;
  }

  if (error) {
    Sentry.captureException(error);
    return (
      <ErrorMessage
        message={`${t`Du har inte behörighet.`} ${deducedError(error)}`}
        padding={24}
      />
    );
  }

  // Reached when `interactiveSignin` skipped the redirect because the page
  // reported itself hidden. A tap proves the page is in use, so this signs in
  // regardless of `document.visibilityState`. A tap while the page reports
  // `hidden` means the device misreports visibility; while `visible`, the
  // `visibilitychange` redirect in `index.tsx` did not happen.
  return (
    <div style={{ padding: 24 }}>
      <FilledButton
        onClick={() => {
          Sentry.captureMessage(
            "Signed in from the RequireGoAuth fallback button",
            {
              level: "warning",
              tags: { visibility_at_click: document.visibilityState },
            },
          );
          void auth.signinRedirect(signinArgs());
        }}
      >
        <Trans>Logga in</Trans>
      </FilledButton>
    </div>
  );
};
